import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { createContactsV2, createRecord } from "../../../apis/ext";
import { useMutation } from "react-query";

import { MagnifyingGlassIcon } from "@radix-ui/react-icons";

import SearchRecordForm from "./SearchRecordForm";
import { isEqual } from "lodash";
import toast from "react-hot-toast";
import { useClickAway } from "@uidotdev/usehooks";
import NoInfo from "../NoInfo";
import { ChevronDown, ChevronUp, Eye, PencilIcon } from "lucide-react";

import RecordRow from "./RecordRow";
import { Button } from "../../ui/button";
import * as Yup from "yup";
import CreateCompanyRecord from "./CreateCompanyRecord";
import { Skeleton } from "../../ui/skeleton";
import { Checkbox } from "../../ui/checkbox";
import { crmRecordKeyValue, truncateValue } from "../../../cf";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";
import Image from "../../Tags/Image";
import { useStoreIntegrations } from "../../../stores/useStoreIntegrations";
import { useCRMGetRecords } from "../../../apiHooks/useCRMGetRecords";
import { useStoreCRMGetRecords } from "../../../stores/useStoreCRMGetRecords";
import { createCRMRecords, createCRMRecordsB } from "../../../apis/crm";
import { useCRMCreateRecords } from "../../../apiHooks/useCRMCreateRecords";
import { normalizeInitialValues } from "../../../utils/GenericFn";

export default function CreateRecord({
  cancel,
  closeModal,
  infoData = {},
  type = "",
  recordCreated,
  persons = [],
  objectApiName = "",
  objectApiLabel = "",
  crmConfigCompany = [],
  crmConfigCompanyForRecordView = [],
  contactRevealed = true,
  source = "",
}) {
  const { activeCRMIntegration } = useStoreIntegrations();
  const captureEvent = useCaptureEvent();
  const [fetchedRecordObj, setFetchedRecordObj] = useState({});
  const [fetchedReferences, setFetchedReferences] = useState([]);
  const [chooseAccountStep, setChooseAccountStep] = useState(false);
  const [chooseAccountStepClicked, setChooseAccountStepClicked] = useState(false);
  const [apiName, setApiName] = useState(objectApiName);
  const [apiLabel, setApiLabel] = useState(objectApiLabel);
  const [initialValues, setInitialValues] = useState({});

  // create contact

  const createContactsV2Fn = useMutation("enrich_profile", createContactsV2, {
    onSuccess(data, variables, context) {
      console.log("createContactsV2Fn data", data);
      if (data && data?.data?.contacts?.length > 0) {
        console.log("variables", variables);
        const contactId = data.data.contacts[0].contact_id;
        if (typeof variables?.onSuccessCallback === "function") {
          variables.onSuccessCallback(contactId); // or pass whole data
        }
      }
    },
    onError(error) {
      console.log(error);
    },
  });

  const handleCreateContactsV2 = (person_id, onSuccessCallback) => {
    if (createContactsV2Fn.isLoading) return;

    createContactsV2Fn.mutate({
      data: {
        contacts: [{ person_id }],
      },
      options: {
        headers: { "X-Workspace-Id": infoData?.activeWorkspaceId },
      },
      onSuccessCallback, // ✅ Pass callback as meta
    });
  };

  if (fetchedReferences && fetchedReferences.length > 0) {
    // console.log("fetchedReferences", fetchedReferences);
    fetchedReferences.forEach((reference) => {
      const selectFieldName = reference.crm_object_api_name; // Use this as the key for select field
      initialValues[selectFieldName] = ""; // Initialize empty select field in form values
    });
  }

  const getChangedValues = (values, initialValues) => {
    const changedValues = {};
    Object.keys(values).forEach((key) => {
      if (!isEqual(values[key], initialValues[key])) {
        changedValues[key] = values[key];
      }
    });
    return changedValues;
  };

  const cleanPayload = (payload) => {
    // Helper function to remove undefined keys
    const cleanObject = (obj) => {
      return Object.keys(obj).reduce((acc, key) => {
        if (obj[key] !== undefined) {
          acc[key] = obj[key];
        }
        return acc;
      }, {});
    };

    // Clean the input_fields and reference_fields objects
    payload.input_fields = cleanObject(payload.input_fields);
    payload.reference_fields = cleanObject(payload.reference_fields);

    // Remove input_fields object if it's empty
    if (Object.keys(payload.input_fields).length === 0) {
      delete payload.input_fields;
    }

    // Remove reference_fields object if it's empty
    if (Object.keys(payload.reference_fields).length === 0) {
      delete payload.reference_fields;
    }

    return payload;
  };

  const getValidationSchema = (fetchedRecordObj) => {
    const shape = {};
    Object.keys(fetchedRecordObj).forEach((key) => {
      const fieldData = fetchedRecordObj[key];
      const inputName = key || "";
      if (fieldData?.required) {
        shape[inputName] = Yup.string().required(`${fieldData?.label} is required`).trim();
      } else {
        shape[inputName] = Yup.string().nullable();
      }
    });
    return Yup.object().shape(shape);
  };

  const formik = useFormik({
    key: apiName,
    initialValues: normalizeInitialValues(initialValues),
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema: getValidationSchema(fetchedRecordObj),
    onSubmit: (inputvalues) => {
      // console.log("Submitted values:", values);
      const values = getChangedValues(inputvalues, formik.initialValues);
      console.log("values", values);
      // if (Object.keys(values).length === 0) {
      //   toast.error("There is no change in the form.");
      //   return;
      // }

      const input_fields = {};
      const reference_fields = {};
      Object.keys(fetchedRecordObj).forEach((key) => {
        const fieldData = fetchedRecordObj[key];
        const inputName = key || "";
        input_fields[inputName] = values[inputName];
      });
      if (fetchedReferences && fetchedReferences.length > 0) {
        fetchedReferences?.forEach((reference) => {
          const selectFieldName = reference.crm_object_api_name;
          reference_fields[selectFieldName] = values[selectFieldName];
        });
      }

      console.log("reference_fields in create", reference_fields);

      const initPayload = {
        crm_object_api_name: apiName,
        type: infoData?.type,
        input_fields: input_fields,
        reference_fields: reference_fields,
      };
      const payload = cleanPayload(initPayload);
      if (type === "company" && initPayload.reference_fields) {
        delete initPayload.reference_fields;
      }
      if (type === "company") {
        payload.organization_id = infoData?.record_id;
      }
      if (type === "person") {
        if (infoData && infoData.person_id) {
          payload.person_id = infoData.person_id;
        }
        if (infoData && infoData.contact_id) {
          payload.contact_id = infoData.contact_id;
        }
      }

      console.log("submit payload", infoData);
      setErrorForCreate({});
      setEditingField(null);

      console.log("payload in create submit", payload);

      if (type === "person") {
        if (infoData.contact_id) {
          handleCRMCreateFunction(payload);
        } else {
          handleCreateContactsV2(infoData?.person_id, (contactId) => {
            payload.contact_id = contactId;
            // if (getContactId) {
            //   getContactId(contactId);
            // }
            handleCRMCreateFunction(payload);
          });
        }
      } else {
        handleCRMCreateFunction(payload);
      }
    },
  });

  useEffect(() => {
    if (fetchedRecordObj && Object.keys(fetchedRecordObj).length > 0) {
      const updatedInitialValues = Object.keys(fetchedRecordObj).reduce((acc, key) => {
        const fieldData = fetchedRecordObj[key]; // Get the inner object
        const fieldName = key || ""; // Ensure inner object exists
        acc[fieldName] = fieldData ? fieldData?.value : ""; // Get the value, fallback to an empty string
        return acc;
      }, {});

      // getValidationSchema(fetchedRecordObj);

      setInitialValues(updatedInitialValues); // Set initial values once object is populated
    }
  }, [fetchedRecordObj]); // Re-run the effect when fetchedRecordObj changes

  const [checkedApiName, setCheckedApiName] = useState("");

  const [errorForCreate, setErrorForCreate] = useState({});

  const { mutate: handleCRMCreate, isLoading: isCRMCreateLoading } = useCRMCreateRecords({
    onStream: (data, variables) => {
      // handleFetchedSearchCRMData(data?.data, variables);
      if (data?.crm_response_info?.status === 200) {
        console.log("onSuccess in create", data);
        cancel();
        closeModal();
        recordCreated(data?.contact_id || "");
        console.log("ECRM_RecordCreated", {
          type: type,
          crm_object_name: apiLabel,
        });
        captureEvent("ECRM_RecordCreated", {
          type: type,
          crm_object_name: apiLabel,
        });
        setTimeout(() => {
          resetStates();
        }, 100);
      }
      if (data?.crm_response_info?.status !== 200) {
        toast.error(data?.crm_response_info?.error?.description);
      }
    },
    onError: (error) => {
      setChooseAccountStepClicked(false);
      console.log(error);
      // handleFetchRecordInCRM(apiName);
      // setCheckedApiName("");
      if (error && error?.data && error?.data?.description && !error?.data?.sytem_field_key) {
        toast.error(error?.data?.description);
      }
      setErrorForCreate(error?.data);
      setEditingField(error?.data?.sytem_field_key);
    },
  });

  const handleCRMCreateFunction = (payload) => {
    handleCRMCreate({
      data: { requests: [{ ...payload }] },
      configId: infoData?.activeCrm,
      options: {
        headers: {
          "X-Workspace-Id": infoData?.activeWorkspaceId,
          "Record-Source": process.env.REACT_APP_APP_NAME,
        },
      },
    });
    // createRecordFn.mutate({
    //   data: { requests: [{ ...payload }] },
    //   configId: infoData?.activeCrm,
    //   options: {
    //     headers: {
    //       "X-Workspace-Id": infoData?.activeWorkspaceId,
    //       "Record-Source": process.env.REACT_APP_APP_NAME,
    //     },
    //   },
    // });
  };

  // create company by checkbox

  const [recordFetchingFromEdit, setRecordFetchingFromEdit] = useState(false);
  const [fetchRecordInCRMLoading, setFetchRecordInCRMLoading] = useState(true);
  // const fetchRecordInCRMFn = useMutation("fetch_record_in_crm", fetchRecordInCRM, {
  //   onSuccess(resdata, variables, context) {
  //     let data = resdata && resdata.length > 0 && resdata[0];
  //     console.log("onSuccess in record", data);
  //     if (data && data.references && data.references.length > 0) {
  //       setFetchedReferences(data.references);
  //       setChooseAccountStep(true);
  //     } else {
  //       setChooseAccountStep(false);
  //     }
  //     if (data && data.data) {
  //       const obj = data.data;
  //       setFetchedRecordObj(obj);
  //     }
  //     setRecordFetchingFromEdit(false);
  //     setEditSearchForCompany(false);
  //     setFetchRecordInCRMLoading(false);
  //   },
  //   onError(error) {
  //     console.log(error);
  //     setRecordFetchingFromEdit(false);
  //     setEditSearchForCompany(false);
  //     setFetchRecordInCRMLoading(false);
  //   },
  // });

  const { crmRecords } = useStoreCRMGetRecords();

  console.log("crmRecords in create", crmRecords);

  const getCRMRecordsFn = useCRMGetRecords({
    onSuccess: (resdata) => {
      let data = resdata && resdata.length > 0 && resdata[0];
      console.log("onSuccess in record", data);
      if (data && data.references && data.references.length > 0) {
        setFetchedReferences(data.references);
        setChooseAccountStep(true);
      } else {
        setChooseAccountStep(false);
      }
      if (data && data.data) {
        const obj = data.data;
        setFetchedRecordObj(obj);
      }
      setRecordFetchingFromEdit(false);
      setEditSearchForCompany(false);
      setFetchRecordInCRMLoading(false);
    },
    onError: (err) => {
      console.log(err);
      setRecordFetchingFromEdit(false);
      setEditSearchForCompany(false);
      setFetchRecordInCRMLoading(false);
    },
  });

  const handleFetchRecordInCRM = (_apiName = apiName, searchParams = {}, search = false) => {
    console.log("record in create", { infoData, _apiName, searchParams });
    let payload = {};
    if (type === "person") {
      let search_params = {
        name: infoData?.company,
        social_media_urls_linkedin_url: infoData?.company_linkedin_url,
      };
      if (searchParams && Object.keys(searchParams).length > 0) {
        search_params = searchParams;
      }
      payload = {
        crm_object_api_name: _apiName,
        type: type,
        person_id: infoData?.person_id,
        contact_id: infoData?.contact_id,
        organization_id: infoData?.organization_id,
        references: [
          {
            type: "company",
            organization_id: infoData?.organization_id,
            ...(search && { search_params }),
          },
        ],
      };
    }
    console.log("infoData organization_id", infoData);
    if (type === "company") {
      payload = {
        organization_id: infoData?.record_id,
        type: type,
        crm_object_api_name: _apiName,
      };
    }
    payload.on_create = true;
    setFetchRecordInCRMLoading(true);
    getCRMRecordsFn.mutate({
      data: [{ ...payload }],
      configId: infoData?.activeCrm,
      options: {
        headers: { "X-Workspace-Id": infoData?.activeWorkspaceId },
      },
    });
  };

  const [companyRecordCreatedByCheckbox, setCompanyRecordCreatedByCheckbox] = useState(false);
  useEffect(() => {
    if (infoData && objectApiName) {
      if (!apiName) {
        setApiName(objectApiName);
        setApiLabel(objectApiLabel);
      }

      if (type === "person") {
        handleFetchRecordInCRM(apiName ? apiName : objectApiName);
      } else {
        handleFetchRecordInCRM(objectApiName);
      }
    }
  }, [objectApiName, companyRecordCreatedByCheckbox]);

  const [open, setOpen] = useState(false);
  const [editSearchForCompany, setEditSearchForCompany] = useState(false);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setEditSearchForCompany(false);
      }, 100);
    }
  }, [open]);

  const resetStates = () => {
    setFetchedRecordObj({});
    setFetchedReferences([]);
    setApiName(objectApiName);
    setApiLabel(objectApiLabel);
    setInitialValues({});
    setCheckedApiName("");
    setRecordFetchingFromEdit(false);
    setOpen(false);
    setEditSearchForCompany(false);
  };

  const ref = useClickAway(() => {
    setEditSearchForCompany(false);
  });

  useEffect(() => {
    if (
      fetchedReferences &&
      fetchedReferences.length > 0 &&
      fetchedReferences[0].records &&
      fetchedReferences[0].records.length > 0
    ) {
      setTimeout(() => {
        formik.setFieldValue(fetchedReferences[0]?.crm_object_api_name, fetchedReferences[0].records[0].Id);
        setCheckedApiName("");
      }, 500);
    }
  }, [fetchedReferences]);

  const [editingField, setEditingField] = useState(null);

  // Function to render each field
  const renderField = (label, inputName, fieldData, custom = false) => {
    return (
      <div
        key={inputName}
        className={`mb-3 ${errorForCreate && errorForCreate?.sytem_field_key == inputName && "error__field"}`}
      >
        {editingField === inputName || (!formik.values[inputName] && fieldData?.required) ? (
          <>
            <p className="mb-2 text-text-700 text-sm field__label">
              {label} {fieldData?.required && <span className="text-red-500">*</span>}
            </p>
            <div
              className="flex justify-between"
              onClick={() => {
                if (!formik.values[inputName] && fieldData?.required) {
                  setEditingField(inputName);
                }
              }}
            >
              {fieldData.picklist_values && fieldData.picklist_values.length > 0 ? (
                <select
                  name={inputName}
                  className="rounded-[8px] w-full bg-background border border-gray-300 text-gray-900 text-[14px] p-2 h-10"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values[inputName] || ""}
                >
                  <option value="">--Select--</option>
                  {fieldData.picklist_values.map((el) => (
                    <option key={el} value={el}>
                      {el}
                    </option>
                  ))}
                </select>
              ) : (
                <textarea
                  name={inputName}
                  className={`rounded-[8px] w-full bg-background border border-gray-300 text-gray-900 text-[14px] p-2 h-10 overflow-x-hidden  ${
                    formik.values[inputName] && formik.values[inputName].length > 60
                      ? "h-[99px]"
                      : "whitespace-pre resize-none"
                  }`}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values[inputName] || ""}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "Return") {
                      e.preventDefault();
                      setEditingField(null);
                      setErrorForCreate({});
                    }
                  }}
                />
              )}
              {!formik.errors[inputName] && (
                <span
                  className="rounded-[8px] bg-background border border-gray-300 text-gray-900 text-[14px] p-2 px-3 h-10 inline-flex items-center justify-center ml-2 cursor-pointer"
                  onClick={() => {
                    setEditingField(null);
                    setErrorForCreate({});
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16.6668 5L7.50016 14.1667L3.3335 10"
                      stroke="var(--brand-700)"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}
            </div>
            {formik.touched[inputName] && formik.errors[inputName] && (
              <p className="text-red-700 text-xs mt-2">{formik.errors[inputName]}</p>
            )}
          </>
        ) : (
          <>
            <p
              className={`mb-2 text-text-700 text-sm group ${
                !formik.values[inputName] &&
                !fieldData?.required &&
                !showAllNonCustomFields &&
                !custom &&
                "hidden"
              }`}
              onClick={() => {
                setEditingField(inputName);
                captureEvent("ECRM_CreateNewForm_EditField", {
                  type: type,
                  field_name: inputName,
                });
              }}
            >
              <span className="w-full flex space-x-2 items-center">
                <span className="text-xs field__label">
                  {" "}
                  {label} {fieldData?.required && <span className="text-red-500">*</span>}
                </span>
                <PencilIcon width={14} height={14} className="ml-2 hidden group-hover:block cursor-pointer" />
              </span>
              <b style={{ wordBreak: "break-all" }} className="block">
                {formik.values[inputName] ? truncateValue(formik.values[inputName]) : "-"}
              </b>
            </p>
          </>
        )}
        {errorForCreate && errorForCreate?.sytem_field_key == inputName && (
          <p className="text-red-700 text-xs mt-2">{errorForCreate?.message}</p>
        )}
      </div>
    );
  };

  const INITIAL_DISPLAY_LIMIT = 5;

  const [showAllNonCustomFields, setShowAllNonCustomFields] = useState(false);
  const [showAllCustomFields, setShowAllCustomFields] = useState(false);

  // Get lists of non-custom and custom fields
  const nonCustomFields = Object.keys(fetchedRecordObj).filter(
    (label) => !fetchedRecordObj[label]?.is_custom
  );
  const customFields = Object.keys(fetchedRecordObj).filter((label) => fetchedRecordObj[label]?.is_custom);

  const countNonCustomFieldsWithValue = () => {
    const data = formik.values;

    console.log("fetchedRecordObj countNonCustomFieldsWithValue", { fetchedRecordObj, data });
    return Object.entries(data).every(([key, value]) => {
      // const referenceField = Object.values(fetchedRecordObj).find((ref) => ref[key] !== undefined);
      const referenceField = fetchedRecordObj[key];

      if (!referenceField) {
        return true;
      }

      const isCustomKey = referenceField.is_custom;

      if (!isCustomKey) {
        return value && typeof value === "string" && value.trim() !== "";
      }
      return true;
    });
  };

  const [contactRevealedForceHide, setContactRevealedForceHide] = useState(false);

  function hasContactInfo(obj) {
    for (const [key, value] of Object.entries(obj)) {
      if (/(mobile|phone|email)/i.test(key)) {
        if (value && typeof value === "string" && value.trim() !== "") {
          return true;
        }
      }
    }
    return false;
  }

  useEffect(() => {
    if (contactRevealedForceHide) {
      return;
    } else if (hasContactInfo(formik.values)) {
      setContactRevealedForceHide(true);
    } else {
      setContactRevealedForceHide(false);
    }
  }, [formik.values]);

  return (
    <div className="p-6 h-screen bg-gray-50">
      <div className="bg-background -mx-6 p-6 border-b border-border -mt-6 mb-4">
        {source !== "app" && (
          <span
            className="flex items-center cursor-pointer mb-4 text-sm space-x-2"
            onClick={() => {
              chooseAccountStepClicked ? setChooseAccountStepClicked(false) : closeModal();
              captureEvent("ECRM_CreateNewForm_BackBtn", {
                type: type,
              });
              setCheckedApiName("");
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M2.5 7.49996H13.75C15.8211 7.49996 17.5 9.17889 17.5 11.25C17.5 13.321 15.8211 15 13.75 15H10M2.5 7.49996L5.83333 4.16663M2.5 7.49996L5.83333 10.8333"
                stroke="var(--gray-600)"
                stroke-width="1.66667"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span>Back</span>
          </span>
        )}
        <div className="flex items-start space-x-2">
          <div>
            <p className="text-sm font-semibold text-gray-900">
              Create a new CRM record - {type === "person" ? "People" : "Company"}
            </p>
          </div>
        </div>
      </div>

      {persons && persons.length > 0 && !chooseAccountStepClicked && (
        <div className="flex justify-between items-center border py-0.5 px-3 rounded-lg bg-background">
          <b className="text-sm">Record type:</b>
          <div className="my-4 flex items-center space-x-4">
            {persons.map((person) => (
              <div className=" flex items-center space-x-2 text-sm">
                <input
                  type="radio"
                  name="person"
                  value={person?.crm_object_api_name}
                  checked={person?.crm_object_api_name === apiName ? true : false}
                  onChange={() => {
                    setFetchedRecordObj({});
                    setChooseAccountStep(false);
                    setChooseAccountStepClicked(false);
                    setApiName(person?.crm_object_api_name);
                    setApiLabel(person?.crm_object_label);
                    handleFetchRecordInCRM(person?.crm_object_api_name);
                    setEditingField(null);
                    setShowAllNonCustomFields(false);
                    setShowAllCustomFields(false);
                    setContactRevealedForceHide(false);
                    setErrorForCreate({});
                    captureEvent("ECRM_CreateNewForm_RadioBtn", {
                      type: type,
                      apiName: person?.crm_object_api_name,
                    });
                  }}
                />{" "}
                <span>{person?.crm_object_label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (checkedApiName) {
            console.log("Company record");
            // createCompanyByCheckbox();
          } else {
            formik.handleSubmit(e);
          }
        }}
        className="mt-4 animate__animated animate__fadeIn pb-6"
      >
        {getCRMRecordsFn.isLoading && !recordFetchingFromEdit ? (
          <>
            <div className="mb-3">
              <Skeleton className="h-[20px] w-[100px] mb-2" />
              <Skeleton className="h-[40px] w-full" />
            </div>
            <div className="mb-3">
              <Skeleton className="h-[20px] w-[100px] mb-2" />
              <Skeleton className="h-[40px] w-full" />
            </div>
            <div className="mb-3">
              <Skeleton className="h-[20px] w-[100px] mb-2" />
              <Skeleton className="h-[40px] w-full" />
            </div>
            <div className="mb-3">
              <Skeleton className="h-[20px] w-[100px] mb-2" />
              <Skeleton className="h-[40px] w-full" />
            </div>
          </>
        ) : (
          <>
            {!chooseAccountStepClicked && (
              <>
                {/* Non-Custom Fields Section */}
                {nonCustomFields.length > 0 && (
                  <div className="non-custom-field-container border p-4 rounded-lg mb-4 pb-1 bg-background">
                    {nonCustomFields.map((key, index) => {
                      const fieldData = fetchedRecordObj[key];
                      const inputName = key || "";

                      // Conditionally render based on display limit
                      const isVisible = showAllNonCustomFields || index < INITIAL_DISPLAY_LIMIT;

                      return (
                        <div
                          key={key}
                          style={{
                            display: isVisible
                              ? "block"
                              : formik.values[inputName] || fieldData?.required
                                ? ""
                                : "none",
                          }}
                        >
                          {renderField(fieldData?.label, inputName, fieldData)}
                        </div>
                      );
                    })}
                    {nonCustomFields.length > INITIAL_DISPLAY_LIMIT && !countNonCustomFieldsWithValue() && (
                      <span
                        className="text-brand-700 text-sm my-2 flex items-center font-bold text-center space-x-2 justify-center cursor-pointer"
                        onClick={() => {
                          setShowAllNonCustomFields(!showAllNonCustomFields);
                          captureEvent("ECRM_CreateNewForm_ShowAllBtn", {
                            type: type,
                            custom: false,
                          });
                        }}
                      >
                        <span>{showAllNonCustomFields ? "Show less" : "Show all"}</span>
                        {showAllNonCustomFields ? (
                          <ChevronUp width={14} height={14} />
                        ) : (
                          <ChevronDown width={14} height={14} />
                        )}
                      </span>
                    )}
                  </div>
                )}

                {customFields.length > 0 && (
                  <div className="custom-field-container border p-4 rounded-lg mb-4 pb-1 bg-background">
                    {customFields.map((key, index) => {
                      const fieldData = fetchedRecordObj[key];
                      const inputName = key || "";

                      // Conditionally render based on display limit
                      const isVisible = showAllCustomFields || index < INITIAL_DISPLAY_LIMIT;

                      return (
                        <div
                          key={key}
                          style={{
                            display: isVisible || fieldData?.required ? "block" : "none",
                          }}
                        >
                          {renderField(fieldData?.label, inputName, fieldData, true)}
                        </div>
                      );
                    })}
                    {customFields.length > INITIAL_DISPLAY_LIMIT && (
                      <span
                        className="text-brand-700 text-sm my-2 flex items-center font-bold text-center space-x-2 justify-center cursor-pointer"
                        onClick={() => {
                          setShowAllCustomFields(!showAllCustomFields);
                          captureEvent("ECRM_CreateNewForm_ShowAllBtn", {
                            type: type,
                            custom: true,
                          });
                        }}
                      >
                        <span>{showAllCustomFields ? "Show less" : "Show all"}</span>
                        {showAllCustomFields ? (
                          <ChevronUp width={14} height={14} />
                        ) : (
                          <ChevronDown width={14} height={14} />
                        )}
                      </span>
                    )}
                  </div>
                )}
              </>
            )}

            {chooseAccountStepClicked &&
              fetchedReferences &&
              fetchedReferences.length > 0 &&
              fetchedReferences.map((reference, index) => {
                const selectFieldNameLabel = reference?.crm_object_label;
                const selectFieldName = reference.crm_object_api_name;

                return (
                  <div className="mb-3" key={index}>
                    <p
                      className={`mb-2 text-text-700 text-sm flex justify-between items-center ${
                        checkedApiName && "hidden"
                      }`}
                    >
                      <span>
                        Choose {selectFieldNameLabel}
                        {reference?.records ? (
                          <span className="border bg-background border-brand-300 text-brand-700 rounded-3xl px-2 py-[1px] text-[10px] ml-2">
                            {reference?.records?.length}
                          </span>
                        ) : (
                          <span className="border bg-background border-brand-300 text-brand-700 rounded-3xl px-2 py-[1px] text-[10px] ml-2">
                            0
                          </span>
                        )}
                      </span>

                      <div className="relative inline-flex" ref={ref}>
                        <span
                          onClick={() => {
                            setEditSearchForCompany(!editSearchForCompany);
                          }}
                          className="cursor-pointer w-[95px] inline-flex items-center whitespace-nowrap text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border bg-background border-brand-300 text-brand-700 px-4 py-2 h-9 rounded-[8px] font-semibold"
                        >
                          <MagnifyingGlassIcon width={20} height={20} className="mr-2" />{" "}
                          <span>{!editSearchForCompany ? "Edit" : "Hide"}</span>
                        </span>
                        {editSearchForCompany && (
                          <div
                            className="absolute top-[40px] right-0  z-10 bg-background   rounded-xl w-[300px] animate__animated animate__fadeIn animate__faster"
                            style={{ boxShadow: "0px 1px 3px var(--gray-400)" }}
                          >
                            <SearchRecordForm
                              formInit={reference?.search_filters_applied}
                              cancelForm={() => {
                                setEditSearchForCompany(false);
                              }}
                              getValues={(values) => {
                                setRecordFetchingFromEdit(true);
                                handleFetchRecordInCRM(apiName, values, true);
                              }}
                              isLoading={recordFetchingFromEdit}
                            />
                          </div>
                        )}
                      </div>
                    </p>

                    {!checkedApiName &&
                      (!reference?.records || (reference.records && reference.records.length === 0)) && (
                        <div className="text-center">
                          <NoInfo heading="" description="We couldn’t find a matching company record" />
                        </div>
                      )}

                    {reference.records &&
                      reference.records.length > 0 &&
                      reference.records.map((el) => {
                        const newItem = {
                          ...el,
                          value: el?.Id,
                          label: el?.Name || el?.data?.name?.value,
                        };

                        return (
                          <>
                            <div
                              className={`p-4 border border-input bg-background rounded-xl mt-4 ${
                                checkedApiName && "opacity-50 pointer-events-none hidden"
                              }`}
                            >
                              <span className="flex w-full items-center justify-between space-x-2">
                                <span className="inline-flex items-center space-x-2">
                                  <Image
                                    src={`/images/crm/${activeCRMIntegration?.integration?.key}-blue.svg`}
                                    className="h-4 no-invert"
                                  />
                                  <span className="text-gray-900 text-sm font-semibold">{newItem.label}</span>
                                  {el?.match_confirmed && (
                                    <Image
                                      className="no-invert"
                                      src="/images/ext/crm/matched.svg"
                                      width={16}
                                      height={16}
                                    />
                                  )}
                                  <a
                                    href={el?.record_url}
                                    target="_blank"
                                    onClick={() => {
                                      captureEvent("ECRM_RedirectToSF", {
                                        type: type,
                                      });
                                    }}
                                  >
                                    {" "}
                                    <Image className="no-invert" src="/images/ext/crm/hyperlink.svg" />
                                  </a>
                                </span>
                                <Checkbox
                                  checked={formik.values[selectFieldName] === newItem?.Id ? true : false}
                                  onCheckedChange={(checked) => {
                                    setCheckedApiName("");
                                    if (formik.values[selectFieldName] === newItem?.Id) {
                                      formik.setFieldValue(selectFieldName, "");
                                    } else {
                                      formik.setFieldValue(selectFieldName, newItem?.Id);
                                    }

                                    setOpen(false);
                                    captureEvent("ECRM_CreateNewChooseRef_ChooseRefCheckbox", {
                                      type: type,
                                    });
                                  }}
                                />
                              </span>

                              <div className="space-y-2 mt-4">
                                {crmConfigCompanyForRecordView &&
                                  crmConfigCompanyForRecordView.length > 0 &&
                                  crmRecordKeyValue(
                                    "company",
                                    selectFieldName,
                                    crmConfigCompanyForRecordView
                                  ) &&
                                  crmRecordKeyValue("company", selectFieldName, crmConfigCompanyForRecordView)
                                    .length > 0 &&
                                  crmRecordKeyValue(
                                    "company",
                                    selectFieldName,
                                    crmConfigCompanyForRecordView
                                  ).map((recordEl) => {
                                    if (recordEl.system_field_name !== "location") {
                                      return (
                                        <>
                                          {recordEl.system_field_name !== "crm_owner_name" && (
                                            <RecordRow
                                              label={recordEl?.label}
                                              value={
                                                recordEl?.crm_field_api_name
                                                  ? recordEl.system_field_name === "company" &&
                                                    newItem?.crm_resolved_company_name
                                                    ? newItem?.crm_resolved_company_name
                                                    : newItem?.data?.[recordEl.system_field_name]?.value
                                                  : ""
                                              }
                                            />
                                          )}
                                          {recordEl.system_field_name === "crm_owner_name" && (
                                            <RecordRow
                                              label={recordEl?.label}
                                              value={newItem?.crm_owner_name}
                                            />
                                          )}
                                        </>
                                      );
                                    }
                                    if (recordEl.system_field_name === "location") {
                                      return (
                                        <RecordRow
                                          label={recordEl?.label}
                                          value={null}
                                          location={true}
                                          locationData={
                                            type === "company"
                                              ? {
                                                  city: newItem?.data?.headquarters_city?.value,
                                                  state: newItem?.data?.headquarters_state?.value,
                                                  country: newItem?.data?.headquarters_country?.value,
                                                }
                                              : {
                                                  city: newItem?.data?.city?.value,
                                                  state: newItem?.data?.state?.value,
                                                  country: newItem?.data?.country?.value,
                                                }
                                          }
                                        />
                                      );
                                    }
                                  })}
                              </div>
                            </div>
                          </>
                        );
                      })}

                    {crmConfigCompany && Array.isArray(crmConfigCompany) && crmConfigCompany.length > 0 && (
                      <>
                        {checkedApiName && (
                          <button
                            onClick={() => {
                              setCheckedApiName("");
                            }}
                            className="cursor-pointer flex w-full justify-center space-x-2 items-center whitespace-nowrap text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border bg-background border-brand-300 text-brand-700 px-4 py-2 h-9 rounded-[8px] font-semibold"
                          >
                            <Eye width={16} height={16} /> <span>Show existing {selectFieldNameLabel} </span>
                            {reference?.records ? (
                              <span className="leading-normal border bg-background border-brand-300 text-brand-700 rounded-3xl px-2 py-[1px] text-[10px] ml-2">
                                {reference?.records?.length}
                              </span>
                            ) : (
                              <span className="leading-normal border bg-background border-brand-300 text-brand-700 rounded-3xl px-2 py-[1px] text-[10px] ml-2">
                                0
                              </span>
                            )}
                          </button>
                        )}
                        {/* {!checkedApiName && (
                            
                          )} */}
                        <div
                          // className={`my-4 flex items-center ${
                          //   formik.values[selectFieldName] &&
                          //   "opacity-50 pointer-events-none"
                          // }`}
                          className="my-4 flex items-center"
                        >
                          <Checkbox
                            checked={checkedApiName ? true : false}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setCheckedApiName(selectFieldName);
                              } else {
                                setCheckedApiName("");
                              }
                              captureEvent("ECRM_CreateNewChooseRef_CreateNewCheckbox", {
                                type: type,
                              });
                            }}
                          />
                          <span className="text-sm text-gray-900 ml-2">
                            Create new {selectFieldNameLabel}:{" "}
                            <span className="font-semibold text-foreground dark:text-white">
                              {infoData?.company}
                            </span>
                          </span>
                        </div>
                        <CreateCompanyRecord
                          infoData={{
                            ...infoData,
                            record_id: infoData?.organization_id,
                          }}
                          apiLabel={selectFieldNameLabel}
                          apiName={selectFieldName}
                          checkedApiName={checkedApiName}
                          goBack={() => {
                            setCheckedApiName("");
                          }}
                          onSuccess={(data) => {
                            console.log("data in onsuccess", data);
                            if (data && data?.Id) {
                              formik.setFieldValue(checkedApiName, data?.Id);
                              setCompanyRecordCreatedByCheckbox(true);
                              setCheckedApiName("");
                              setTimeout(() => {
                                formik.handleSubmit();
                              }, 200);
                            }
                          }}
                        />
                      </>
                    )}
                  </div>
                );
              })}
          </>
        )}

        {!contactRevealed && !contactRevealedForceHide && !fetchRecordInCRMLoading ? (
          <div className="flex items-start bg-warning-100 p-3 border border-gray-200 space-x-2 text-sm rounded-md mb-4">
            <Image className="no-invert" src="/images/ext/crm/reveal-warning.svg" />
            <div>
              <h3 className="text-foreground font-semibold">Contact details missing</h3>
              <p className="text-gray-500 mt-1">
                It is recommended to reveal or add contact details before creating a CRM record
              </p>
              <div className="flex items-center mt-3">
                <button
                  className="text-[14px] px-5 flex  justify-center items-center py-2 h-9 space-x-2  rounded-[8px] text-white bg-brand-600 dark:bg-brand-300 border border-brand-600 "
                  variant="purple"
                  onClick={() => {
                    closeModal();
                    captureEvent("ECRM_CreateNewForm_DetailsMissingGoBackBtn", {
                      type: type,
                    });
                  }}
                >
                  Go back
                </button>
                <Button
                  variant="link"
                  onClick={() => {
                    captureEvent("ECRM_CreateNewForm_DetailsMissingProceedBtn", {
                      type: type,
                    });
                    setContactRevealedForceHide(true);
                  }}
                >
                  Proceed
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {!checkedApiName && (
              <div className="flex items-center mt-4 space-x-2">
                {chooseAccountStep ? (
                  <>
                    {chooseAccountStepClicked ? (
                      <button
                        type="submit"
                        disabled={isCRMCreateLoading || getCRMRecordsFn.isLoading || !formik.isValid}
                        className="text-[14px] flex-1 disabled:opacity-70 flex w-8/12 justify-center items-center py-2 h-9 space-x-2 px-1 rounded-[8px] text-white bg-brand-600 dark:bg-brand-300 border border-brand-600 "
                        onClick={() => {
                          captureEvent("ECRM_CreateNewForm_Submit", {
                            type: type,
                            crm_object_name: apiLabel,
                          });
                        }}
                      >
                        Create new record
                        {isCRMCreateLoading && <div className="loader_circle ml-2 inline-block"></div>}
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setChooseAccountStepClicked(true);
                          captureEvent("ECRM_CreateNewForm_NextBtn", {
                            type: type,
                          });
                        }}
                        disabled={getCRMRecordsFn.isLoading || !formik.isValid}
                        className="text-[14px] flex-1 disabled:opacity-70 flex w-8/12 justify-center items-center py-2 h-9 space-x-2 px-1 rounded-[8px] text-white bg-brand-600 dark:bg-brand-300 border border-brand-600 "
                      >
                        Next
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    type="submit"
                    disabled={isCRMCreateLoading || getCRMRecordsFn.isLoading || !formik.isValid}
                    className="text-[14px] flex-1 disabled:opacity-70 flex w-8/12 justify-center items-center py-2 h-9 space-x-2 px-1 rounded-[8px] text-white bg-brand-600 dark:bg-brand-300 border border-brand-600 "
                    onClick={() => {
                      captureEvent("ECRM_CreateNewForm_Submit", {
                        type: type,
                        crm_object_name: apiLabel,
                      });
                    }}
                  >
                    Create new record
                    {isCRMCreateLoading && <div className="loader_circle ml-2 inline-block"></div>}
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    chooseAccountStepClicked ? setChooseAccountStepClicked(false) : cancel();

                    captureEvent(
                      chooseAccountStepClicked
                        ? "ECRM_CreateNewChooseRef_BackBtn"
                        : "ECRM_CreateNewForm_CancelBtn",
                      {
                        type: type,
                      }
                    );
                  }}
                  className="inline-flex  w-4/12 justify-center items-center whitespace-nowrap text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50  bg-background border text-gray-600 px-4 py-2 h-9 rounded-[8px]"
                >
                  {chooseAccountStepClicked ? "Back" : "Cancel"}
                </button>
              </div>
            )}
          </>
        )}
      </form>
    </div>
  );
}
